import type { BuildingInteriorSpec } from "@cold-case/shared/interior-layout";
import {
  interiorThemeCacheKey,
  resolveInteriorTheme,
} from "@cold-case/shared/building-interior-theme";
import { TILE_SIZE } from "@cold-case/shared/constants";
import Phaser from "phaser";

import { bakeInteriorGroundCanvas } from "./interior-renderer.js";

const INTERIOR_GROUND_TEX_VERSION = "v2";

export class InteriorMapGrid {
  readonly walkable: boolean[][];
  readonly worldWidth: number;
  readonly worldHeight: number;

  constructor(
    public readonly spec: BuildingInteriorSpec,
    private readonly scene: Phaser.Scene,
    buildingId: string,
    downtown: boolean
  ) {
    this.walkable = spec.walkable;
    this.worldWidth = spec.gridW * TILE_SIZE;
    this.worldHeight = spec.gridH * TILE_SIZE;
    this._theme = resolveInteriorTheme(buildingId, downtown);
    this._cacheKey = `${interiorThemeCacheKey(buildingId, downtown)}_${buildingId}`;
  }

  private readonly _theme;
  private readonly _cacheKey: string;

  render(): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, 0);
    const texKey = `interior_ground_${this._cacheKey}_${INTERIOR_GROUND_TEX_VERSION}`;
    if (!this.scene.textures.exists(texKey)) {
      const canvas = bakeInteriorGroundCanvas(this.spec, this._theme);
      this.scene.textures.addCanvas(texKey, canvas);
      this.scene.textures.get(texKey)?.setFilter(Phaser.Textures.FilterMode.LINEAR);
    }
    const tile = this.scene.add.tileSprite(0, 0, this.worldWidth, this.worldHeight, texKey);
    tile.setOrigin(0, 0);
    container.add(tile);
    container.setDepth(0);
    return container;
  }

  isWalkableTile(tx: number, ty: number): boolean {
    if (tx < 0 || ty < 0 || tx >= this.spec.gridW || ty >= this.spec.gridH) return false;
    return this.walkable[ty]![tx]!;
  }

  pixelToTile(px: number, py: number): { tx: number; ty: number } {
    return {
      tx: Math.floor(px / TILE_SIZE),
      ty: Math.floor(py / TILE_SIZE),
    };
  }
}
